/**
 * `/onboarding`: profile completion form, gating access to the app until
 * `first_name` and `last_name` are filled in (see {@link isProfileComplete}
 * and `docs/AUTH.md`).
 *
 * The same form doubles as the profile editor at `/onboarding?edit=1`, which
 * the account menu links to: consent was already given at signup, so that
 * mode drops the one-off RGPD checkbox and returns to the app once saved.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { Button, Card, Checkbox, Input } from '../components/ui';
import { t } from '../i18n/fr';

interface FormState {
  first_name: string;
  last_name: string;
  phone: string;
  rgpd: boolean;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

/**
 * Render the onboarding form.
 *
 * @returns The page element.
 */
export function OnboardingPage() {
  const { user, profileComplete, updateProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editing = searchParams.get('edit') === '1';
  const [form, setForm] = useState<FormState>({
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    phone: user?.phone ?? '',
    rgpd: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  if (profileComplete && !editing) {
    return <Navigate to="/" replace />;
  }

  function validate(): FormErrors {
    const nextErrors: FormErrors = {};
    if (!form.first_name.trim()) {
      nextErrors.first_name = t('onboarding.errors.firstName');
    }
    if (!form.last_name.trim()) {
      nextErrors.last_name = t('onboarding.errors.lastName');
    }
    if (!editing && !form.rgpd) {
      nextErrors.rgpd = t('onboarding.errors.rgpd');
    }
    return nextErrors;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setSubmitting(true);
    try {
      await updateProfile({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
      });
      if (editing) {
        navigate('/', { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-screen">
      <Card>
        <h1>{editing ? t('profile.title') : t('onboarding.title')}</h1>
        <p>{editing ? t('profile.lead') : t('onboarding.lead')}</p>
        <form onSubmit={(event) => void onSubmit(event)} noValidate>
          <Input
            label={t('onboarding.firstName')}
            value={form.first_name}
            onChange={(event) => setForm({ ...form, first_name: event.target.value })}
            error={errors.first_name}
            required
          />
          <Input
            label={t('onboarding.lastName')}
            value={form.last_name}
            onChange={(event) => setForm({ ...form, last_name: event.target.value })}
            error={errors.last_name}
            required
          />
          <Input
            label={t('onboarding.phone')}
            type="tel"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
            autoComplete="tel"
          />
          {editing ? null : (
            <Checkbox
              label={t('onboarding.rgpd')}
              checked={form.rgpd}
              onChange={(event) => setForm({ ...form, rgpd: event.target.checked })}
              error={errors.rgpd}
            />
          )}
          <Button type="submit" fullWidth disabled={submitting}>
            {submitting ? t('common.saving') : editing ? t('common.save') : t('onboarding.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
